import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Player } from "@lottiefiles/react-lottie-player";
import * as challengeRepo from "../../shared/repos/graphql/challenge";
import ddLog from '../../shared/utilities/datadog/log';
import legacyStyles from "./LoyaltyReward.module.css";
import brandStyles from "./LoyaltyRewardBrandRefresh.module.css";
import dotLine from "./imgs/dot-line.png";
import step1Bg from "./imgs/step-1-bg.png";
import dripImage from "./imgs/drip.png";
import infoIcon from "./imgs/info-icon.png";
import Loader from "../../components/Loader/Loader";
import LinkWithCountry from "../../components/LinkWithCountry/LinkWithCountry";

const CookieChallenge = ({ variant = "simple", onChallengeLoaded, brandRefresh = false }) => {
  const styles = brandRefresh ? brandStyles : legacyStyles;
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChallenge = async () => {
      let data = null; // Declare outside for access in finally
      try {
        const res = await challengeRepo.getChallenge();
        data = res?.data?.getChallenge || null;
        if (data) {
          setChallenge(data);
        }
      } catch (error) {
        ddLog.logError(error);
      } finally {
        if (brandRefresh) setLoading(false);
        if (typeof onChallengeLoaded === "function") {
          onChallengeLoaded(!!data); // !!data is cleaner than ternary
        }
        if (!brandRefresh) setLoading(false);
      }
    };
    fetchChallenge();
  }, [onChallengeLoaded, brandRefresh]);

  if (loading) return <Loader />;
  if (!challenge) return null;

  const {
    title,
    description,
    learnMoreText,
    customerLevel,
    actionCountRequired,
    emptyStateImage,
    rewardImage,
    rewardCompleteImage,
    explanation,
    step1Image,
    step1Title,
    step1Description,
    step2Image,
    step2Title,
    step2Description,
  } = challenge;


  const renderImage = (url, stepTitle, className=null) => {
    if (!url) return null;
    const ext = url.split(".").pop().toLowerCase();

    return ext === "json"
      ? <Player autoplay loop src={url} className={className} />
      : <img src={url} alt={stepTitle} className={className} />;
  };

  const renderProgress = (height, width) => (
    <div className="flex flex-wrap justify-center items-center relative top-[-20px] w-full">
      {Array.from({ length: actionCountRequired }).map((_, i) => {
        const step = i + 1;
        const isComplete = i < customerLevel;
        const imageKey = isComplete
          ? `rewardStep${step}CompleteImage`
          : `rewardStep${step}Image`;

        const src =
            challenge[imageKey] ||
            (isComplete ? rewardCompleteImage : rewardImage);
        return (
          <div
            className="animate-drop opacity-0"
            style={{
              height: `${height}px`,
              width: `${width}px`,
              animationDelay: `${i * 300}ms`
            }}
          >
            {renderImage(src, `step ${step}`)}
          </div>
        );
      })}
    </div>
  );

  const renderToolTip = () => (
    <span
      className={`${styles.tooltipHover} right-[-16px] tooltip rounded shadow-lg p-1 bg-[#ce4396] text-white mt-6 px-2 py-1 text-[12px] w-full min-w-[343px] text-justify z-[11]`}
    >
      {learnMoreText}
    </span>
  );

  const renderInfoIcon = () => (
    <span className="has-tooltip relative z-[11]">
      <img alt="offer info" src={infoIcon} height={10} width={10} />
      {renderToolTip()}
    </span>
  );

  const renderHeader = () => {
    const [firstLine, secondLine] = title?.split("\\n") || [];

    const renderDescriptionBlock = () => (
      <div className="flex items-center justify-center gap-[6px] mt-1">
        <p className="font-[900] text-[27px] tracking-[-0.2px] font-congenialBlack">
          {description}
        </p>
        {renderInfoIcon()}
      </div>
    );

    if (variant === "detailed") {
      return (
        <div className="z-10 text-white text-center mb-5 mt-7">
          <p className="flex flex-col gap-[4px]">
            <span className="bg-[#F5C416] rounded-xl text-black font-bold pt-1 pb-0.5 px-3 max-w-max mx-auto font-congenialBlack">
              {firstLine}
            </span>
            <span className="font-montserratMedium">{secondLine}</span>
          </p>
          {renderDescriptionBlock()}
        </div>
      );
    }

    return (
      <div className="z-10 text-white text-center pt-4 px-[15px] pb-[18px]">
        <p className="text-[14px] font-bold font-montserratBold">
          {title?.replace(/\\n/g, "")}
        </p>
        {renderDescriptionBlock()}
      </div>
    );
  };


  const renderViewMore = () => (
    <div className="z-10 w-full text-center mt-4">
      <LinkWithCountry
        to="/user/loyalty-rewards"
        className="inline-block px-6 py-2 text-base text-white bg-dark rounded-full font-semibold shadow-md hover:bg-opacity-90 transition"
      >
        View More
      </LinkWithCountry>
    </div>
  );

  const StepDetail = ({ image, stepTitle, stepDescription, bg, imageClass, containerClass }) => (
    <div className={`relative flex flex-col items-center justify-center gap-[6px] w-[343px]  ${containerClass}`}>
      {bg ? (
        <div className="flex justify-center items-center relative">
          <img src={bg} alt="" className="max-w-[154px] w-[154px] h-[154px]" />
          {renderImage(image, stepTitle, imageClass,)}
        </div>
      ) : (
        renderImage(image,stepTitle, imageClass)
      )}
      <p className="text-center text-white font-bold leading-5 mt-[7px]">{stepTitle}</p>
      <p
        className="text-center text-[#B4A5BB] text-sm leading-5"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: stepDescription.replace(/\r?\n/g, '<br />'),
        }}
      />
    </div>
  );

  StepDetail.propTypes = {
    image: PropTypes.string,
    stepTitle: PropTypes.string,
    stepDescription: PropTypes.string,
    bg: PropTypes.string,
    imageClass: PropTypes.string,
    containerClass: PropTypes.string,
  };

  StepDetail.defaultProps = {
    image: "",
    stepTitle: "",
    stepDescription: "",
    bg: null,
    imageClass: "",
    containerClass: "",
  };

  const renderExplanation = () => (
    <div className="z-10 w-full bg-glow absolute bottom-0 rounded-b-[20px] px-5 py-2 text-black text-center font-montserratMedium">
      <p>{explanation}</p>
    </div>
  );

  const renderDripAnimation = () => (
    <div className="w-full absolute bottom-0 rounded-b-[20px] px-5 py-2 text-sm">
      <img
        src={dripImage}
        alt="final-drop"
        width={65}
        className="absolute left-[20px] top-0 opacity-0 animate-drop z-0"
        style={{  animationDelay: `${customerLevel > 0 ? actionCountRequired * 300 : 300}ms`, animationFillMode: "forwards" }}
      />
    </div>
  );

  const renderEmptyImage = () => (
    <div className="flex flex-wrap justify-center items-center relative top-[-20px] w-full">
      {renderImage(emptyStateImage, "Empty Reward", "h-[70px]")}
    </div>
  );

  const renderDetails = () => (
    <div className="details pt-[62px] relative">
      <img src={dotLine} alt="" className="absolute w-full max-w-[163px] h-[468px] top-0 right-[14px]" />
      {[
        {
          key: "step1",
          image: step1Image,
          stepTitle: step1Title,
          stepDescription: step1Description,
          bg: step1Bg,
          imageClass: "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130px]",
          containerClass: "",
        },
        {
          key: "step2",
          image: step2Image,
          stepTitle: step2Title,
          stepDescription: step2Description,
          imageClass: "w-[245px] h-[186px]",
          containerClass: "mt-[129px]",
        },
      ].map((step) => (
        <StepDetail
          key={step.key}
          image={step.image}
          stepTitle={step.stepTitle}
          stepDescription={step.stepDescription}
          bg={step.bg}
          imageClass={step.imageClass}
          containerClass={step.containerClass}
        />
      ))}
    </div>
  );

  const renderDetailedView = () => (
    <div className="flex flex-col items-center justify-center mb-10">
      <div className={`${styles.loyaltyPoints} bg-cover relative !h-[383px]`}>
        {renderHeader()}
        <div className="z-10 px-2 absolute w-full">{renderProgress(105, 105)}</div>
        <p className="w-max text-white text-center absolute left-1/2 bottom-[24px] -translate-x-1/2">
          {`${customerLevel}/${actionCountRequired} ice cream trips`}
        </p>
      </div>
      {renderDetails()}
    </div>
  );

  const renderSimpleView = () => (
    <div className="flex flex-col justify-center items-center">
      <div className={`${styles.loyaltyPoints} bg-cover relative`}>
        {renderHeader()}
        <div className="z-10 px-2 absolute w-full">
          {customerLevel === 0 && !!emptyStateImage ? renderEmptyImage() : renderProgress(65, 65)}
        </div>
        {renderExplanation()}
        {renderDripAnimation()}
      </div>
      {renderViewMore()}
    </div>
  );

  return variant === "detailed" ? renderDetailedView() : renderSimpleView();
};

CookieChallenge.propTypes = {
  brandRefresh: PropTypes.bool,
  variant: PropTypes.oneOf(["simple", "detailed"]),
  onChallengeLoaded: PropTypes.func,
};

export default CookieChallenge;
